import React, { useMemo } from 'react';
import {
  type ColumnDef,
  createColumnHelper,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, Code2, Maximize2, Pin } from 'lucide-react';
import { isExplicitlyNotJsonColumn, looksLikeJsonString } from '../utils/jsonDetection';
import type { JsonClassification } from '@/features/sql-resolver/types/schema';
import { EnumHeaderGlyph } from '../components/grid-glyphs/EnumHeaderGlyph';
import { JsonHeaderGlyph } from '../components/grid-glyphs/JsonHeaderGlyph';
import { SpatialHeaderGlyph, type SpatialGlyphState } from '../components/grid-glyphs/SpatialHeaderGlyph';
import { SpatialCellRenderer } from '../components/cells/SpatialCellRenderer';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import { shouldShowExpandIconForQueryResults } from '../../table-explorer/utils/expandedEditDetection';
import { ColumnProfileBadge } from '../components/ColumnProfileBadge';
import { LazyForeignKeyCell } from '../../query-editor/components/LazyForeignKeyCell';
import { ArrayFkCell } from '../components/ArrayFkCell';
import { JsonCellRenderer } from '../components/cells/JsonCellRenderer';
// PP-10 — shared with `TableExplorerGrid.tsx` so the two grids can't drift again.
import { CELL_EXPAND_BUTTON_CLASS, CELL_EXPAND_WRAPPER_CLASS } from '../components/cells/cellExpandButton';
import { JsonLooseFkBadges } from '../components/filters/JsonLooseFkBadges';
import { withLookupLabel } from '../components/cells/LookupLabelSuffix';
import type { LookupLabelInfo } from './useLookupLabelMaps';
import { findLooseRelationshipsForColumn } from '../utils/looseFkResolver';
import type { ExpandedSpatialPayload, SpatialColumnDeclaration } from '../utils/spatialValueAccessor';
import { useMetaInfo } from './useMetaInfo';
import type { ColumnProfileStats } from '../utils/columnProfiling';
import { openDataProfileTab } from '../../data-profile/services/dataProfileNavigation';
import { formatNumericDisplay, type NumericGroupingMode } from '../utils/columnAlignment';

/** Normalized column metadata used across results grid modules */
export type NormalizedColumn = {
  id: string;
  key: string;
  name: string;
  dataType: string | undefined;
  isPrimaryKey: boolean | undefined;
  isForeignKey: boolean | undefined;
  /**
   * True when the FK is user-declared via MetaInfo (loose relationship), not a real DB constraint.
   * Optional: when undefined, `LazyForeignKeyCell` falls back to a MetaInfo lookup.
   */
  isLoose?: boolean;
  sourceSchema: string | undefined;
  sourceTable: string | undefined;
  sourceColumn: string | undefined;
  showSourceTable: boolean;
  /** JSON classification from the schema metadata (JCS § Slice E1). */
  jsonClassification?: JsonClassification;
};

/** Minimal edit-mode info accessed via ref from cell renderers */
export type EditModeSnapshot = {
  isEditMode: boolean;
  isColumnEditable: (columnName: string) => boolean;
};

type UseCellRendererParams = {
  normalizedColumns: NormalizedColumn[];
  sortState: { columnId: string; direction: 'asc' | 'desc' } | null;
  jsonColumnIdSet: Set<string>;
  /**
   * Column ids that are JSON by type (native JSON column or user-declared).
   * Slice 4: these always render as JSON cells regardless of value heuristic.
   */
  forcedJsonColumnIds?: Set<string>;
  /**
   * Column ids that are JSON by user declaration on a text column. When parsing
   * fails for one of these, the cell shows a "!" glyph and falls back to raw text.
   */
  declaredJsonColumnIds?: Set<string>;
  /**
   * Column ids whose engine type is a real JSON type (`jsonb`, `pg json`,
   * `mysql json`, `mssql 2022+ json`, `oracle 21c+ json`). Rendered with a
   * muted gray `{}` clickable glyph that opens the configure-JSON dialog in
   * native mode.
   */
  nativeJsonColumnIds?: Set<string>;
  /**
   * Column ids that are `json-eligible` AND have JSON-looking values in the
   * loaded rows. Rendered with a muted gray `{}?` clickable glyph that opens
   * the declare-JSON dialog in eligible mode. When a column is also declared
   * or native, the declared/native glyph takes priority and this set's entry
   * is ignored.
   */
  declarableJsonColumnIds?: Set<string>;
  /** Click handler for the declared (blue `{}`) glyph. */
  onDeclaredGlyphClick?: (columnId: string) => void;
  /** Click handler for the native (gray `{}`) glyph. */
  onNativeGlyphClick?: (columnId: string) => void;
  /** Click handler for the declarable (gray `{}?`) glyph. */
  onDeclarableGlyphClick?: (columnId: string) => void;
  /** Click handler for the teal loose-FK header badge — opens the editor dialog. */
  onLooseFkGlyphClick?: (columnId: string) => void;
  /** Column ids with an enum declaration → blue ▾ glyph. */
  declaredEnumColumnIds?: Set<string>;
  /** Column ids whose engine type is a native enum → gray ▾ glyph. */
  nativeEnumColumnIds?: Set<string>;
  /** Column ids that are enum-eligible AND name-hinted → gray ▾? glyph. */
  hintedEnumColumnIds?: Set<string>;
  /** Click handler for any enum glyph; mode distinguishes the three states. */
  onEnumGlyphClick?: (columnId: string, mode: 'declared' | 'native' | 'eligible') => void;
  /**
   * Engine id — required to sniff spatial cell values (EWKB / SpatiaLite / …).
   * When undefined, spatial rendering is skipped (the spatial sets are empty).
   */
  engine?: DatabaseEngineId;
  /** Column ids whose engine type is a native spatial type → gray ⌖ glyph + summary cell. */
  nativeSpatialColumnIds?: Set<string>;
  /** Column ids declared spatial via MetaInfo (T18) → blue ⌖ glyph. */
  declaredSpatialColumnIds?: Set<string>;
  /** columnId → declaration (T18) — powers the declared cell's row-aware assembly + peek decode. */
  declaredSpatialColumns?: Map<string, SpatialColumnDeclaration>;
  /** Column ids that look spatial but aren't declared (T19) → gray ⌖? glyph. */
  spatialDeclarableColumnIds?: Set<string>;
  /** Click handler for any spatial header glyph; state distinguishes the three cases. */
  onSpatialGlyphClick?: (columnId: string, state: SpatialGlyphState) => void;
  /**
   * Windowed-mode full-value fetch for spatial cells (T11/T26 bounded batch).
   * When provided AND the cell value is a truncated string preview, the peek /
   * expanded-dialog map decode the FULL value instead of the preview. Absent on
   * non-windowed grids and for already-complete marker objects.
   */
  getFullSpatialValue?: (rowIndex: number, columnId: string) => Promise<unknown>;
  openExpandedTextDialog: (
    title: string, value: string, columnId?: string,
    rowData?: Record<string, unknown>, sourceRowIndex?: number,
    spatial?: ExpandedSpatialPayload,
  ) => void;
  isColumnPinned: (id: string) => string | false;
  unpinColumn: (id: string) => void;
  showColumnProfiling: boolean;
  columnProfiles: Map<string, ColumnProfileStats>;
  rows: Record<string, unknown>[];
  connectionId?: string;
  database?: string;
  editModeRef: React.RefObject<EditModeSnapshot | undefined>;
  minColumnWidth: number;
  /** Surface label propagated to FK-cell analytics. Defaults to 'query_editor' if undefined. */
  analyticsSurface?: 'query_editor' | 'table_explorer';
  /**
   * PP-01 — column ids whose engine type is numeric. Header label is
   * right-aligned; the `<td>` alignment itself lives in `ResultsGrid`.
   */
  numericColumnIds?: ReadonlySet<string>;
  /**
   * PP-01 — numeric column ids that are quantities (not keys/ordinals) and so
   * get locale thousands grouping **for display only**. Copy/export keep the
   * raw value; see `columnAlignment.ts`.
   */
  groupedNumericColumnIds?: ReadonlySet<string>;
  /** PP-01 — how grouped columns render (`useAppStore.numericGrouping`, read once by the grid). */
  numericGrouping?: NumericGroupingMode;
  /**
   * Lookup-enum key→label pairs per column id (`useLookupLabelMaps`). An FK
   * cell on one of these columns renders a muted `· <label>` suffix after the
   * link — presentation only, never in the copied/exported value. Mirrored in
   * `TableExplorerGridRow`.
   */
  lookupLabelInfos?: Map<string, LookupLabelInfo>;
};

const columnHelper = createColumnHelper<Record<string, unknown>>();

const MAX_ARRAY_FK_PILLS = 5;

const MAX_TITLE_ATTR_CHARS = 512;

const EMPTY_SET: Set<string> = new Set();

function truncateForTitle(s: string): string {
  if (s.length <= MAX_TITLE_ATTR_CHARS) return s;
  return `${s.slice(0, MAX_TITLE_ATTR_CHARS)}…`;
}

export function useCellRenderer({
  normalizedColumns,
  sortState,
  jsonColumnIdSet,
  forcedJsonColumnIds,
  declaredJsonColumnIds,
  nativeJsonColumnIds,
  declarableJsonColumnIds,
  onDeclaredGlyphClick,
  onNativeGlyphClick,
  onDeclarableGlyphClick,
  onLooseFkGlyphClick,
  declaredEnumColumnIds,
  nativeEnumColumnIds,
  hintedEnumColumnIds,
  onEnumGlyphClick,
  engine,
  nativeSpatialColumnIds,
  declaredSpatialColumnIds,
  declaredSpatialColumns,
  spatialDeclarableColumnIds,
  onSpatialGlyphClick,
  getFullSpatialValue,
  openExpandedTextDialog,
  isColumnPinned,
  unpinColumn,
  showColumnProfiling,
  columnProfiles,
  rows,
  connectionId,
  database,
  editModeRef,
  minColumnWidth,
  analyticsSurface,
  numericColumnIds,
  groupedNumericColumnIds,
  numericGrouping,
  lookupLabelInfos,
}: UseCellRendererParams): ColumnDef<Record<string, unknown>>[] {
  const metaInfoQuery = useMetaInfo(connectionId ?? null, database ?? null);
  const metaInfo = metaInfoQuery.data;
  return useMemo<ColumnDef<Record<string, unknown>>[]>(() =>
    normalizedColumns.map((column) => {
      const isNumeric = numericColumnIds?.has(column.id) ?? false;
      const isGroupedNumeric = groupedNumericColumnIds?.has(column.id) ?? false;
      return columnHelper.accessor((row) => row[column.key], {
        id: column.id,
        header: () => (
          <div className={`relative flex flex-col leading-tight ${isNumeric ? 'items-end' : ''} ${column.showSourceTable && column.sourceTable ? 'pt-3' : ''}`}>
            {column.showSourceTable && column.sourceTable && (
              <span
                className={`absolute top-0 left-0 right-0 text-[9px] leading-none text-muted-foreground truncate ${isNumeric ? 'text-right' : ''}`}
                title={column.sourceTable}
              >
                {column.sourceTable}
              </span>
            )}
            <span className="flex items-center gap-1">
              {column.isPrimaryKey && (
                <abbr
                  className="text-amber-700 dark:text-amber-400 text-[10px] no-underline"
                  title="Primary Key"
                  aria-label="Primary Key"
                >
                  PK
                </abbr>
              )}
              {column.isForeignKey && (
                column.isLoose && onLooseFkGlyphClick ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onLooseFkGlyphClick(column.id);
                    }}
                    className="text-teal-500 text-[10px] italic leading-none cursor-pointer hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                    title="Loose Foreign Key (user-declared) — click to edit"
                    aria-label="Loose Foreign Key (user-declared) — click to edit"
                    data-testid="grid-header-loose-fk-prefix"
                  >
                    FK
                  </button>
                ) : (
                  <abbr
                    className={
                      column.isLoose
                        ? 'text-teal-500 text-[10px] italic no-underline'
                        : 'text-blue-500 text-[10px] no-underline'
                    }
                    title={column.isLoose ? 'Loose Foreign Key (user-declared)' : 'Foreign Key'}
                    aria-label={column.isLoose ? 'Loose Foreign Key (user-declared)' : 'Foreign Key'}
                  >
                    FK
                  </abbr>
                )
              )}
              {declaredJsonColumnIds?.has(column.id) ? (
                <JsonHeaderGlyph state="declared" onClick={() => onDeclaredGlyphClick?.(column.id)} />
              ) : nativeJsonColumnIds?.has(column.id) ? (
                <JsonHeaderGlyph state="native" onClick={() => onNativeGlyphClick?.(column.id)} />
              ) : declarableJsonColumnIds?.has(column.id) ? (
                <JsonHeaderGlyph state="declarable" onClick={() => onDeclarableGlyphClick?.(column.id)} />
              ) : null}
              {declaredEnumColumnIds?.has(column.id) ? (
                <EnumHeaderGlyph state="declared" onClick={() => onEnumGlyphClick?.(column.id, 'declared')} />
              ) : nativeEnumColumnIds?.has(column.id) ? (
                <EnumHeaderGlyph state="native" onClick={() => onEnumGlyphClick?.(column.id, 'native')} />
              ) : hintedEnumColumnIds?.has(column.id) ? (
                <EnumHeaderGlyph state="eligible" onClick={() => onEnumGlyphClick?.(column.id, 'eligible')} />
              ) : null}
              {nativeSpatialColumnIds?.has(column.id) ? (
                <SpatialHeaderGlyph state="native" onClick={() => onSpatialGlyphClick?.(column.id, 'native')} />
              ) : declaredSpatialColumnIds?.has(column.id) ? (
                <SpatialHeaderGlyph state="declared" onClick={() => onSpatialGlyphClick?.(column.id, 'declared')} />
              ) : spatialDeclarableColumnIds?.has(column.id) ? (
                <SpatialHeaderGlyph state="declarable" onClick={() => onSpatialGlyphClick?.(column.id, 'declarable')} />
              ) : null}
              {isColumnPinned(column.id) && (
                <button
                  type="button"
                  className="inline-flex items-center justify-center text-muted-foreground hover:text-foreground flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  title="Unpin column"
                  aria-label="Unpin column"
                  onClick={(e) => {
                    e.stopPropagation();
                    unpinColumn(column.id);
                  }}
                >
                  <Pin className="h-3 w-3" aria-hidden="true" />
                </button>
              )}
              <span>{column.name}</span>
              {sortState?.columnId === column.id && (
                sortState.direction === 'asc' ? (
                  <ArrowUp className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
                ) : (
                  <ArrowDown className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
                )
              )}
            </span>
            {showColumnProfiling && columnProfiles.has(column.id) && (
              <ColumnProfileBadge
                stats={columnProfiles.get(column.id)!}
                rows={rows}
                onOpenFullProfile={
                  column.sourceTable && connectionId
                    ? () =>
                        openDataProfileTab({
                          connectionId,
                          database: database ?? '',
                          schema: column.sourceSchema ?? '',
                          table: column.sourceTable!,
                          focusedColumn: column.sourceColumn ?? column.name,
                          dataMode: 'sample',
                          entryPoint: 'popover_footer',
                        })
                    : undefined
                }
              />
            )}
          </div>
        ),
        minSize: minColumnWidth,
        // NO HOOKS IN HERE. `ResultsGridDataRow.renderCellContent` invokes
        // this as a PLAIN FUNCTION (deliberately — mounting it as a component
        // made the closure a React element type, so every rebuild of this memo
        // remounted the cell and wiped open popovers). A hook added below would
        // therefore be a conditional hook call. Emit stateful pieces as JSX of
        // a module-level component instead, the way this already does.
        cell: ({ getValue, row }) => {
          const value = getValue();
          if (value === null || value === undefined) {
            return <span className="text-muted-foreground/60 italic text-xs">NULL</span>;
          }
          // Array loose-FK: each element is a clickable lookup. We restrict to
          // loose FKs because real FKs in our supported engines cannot be array-
          // typed. Edit-mode short-circuits here too — array cells are read-only
          // navigation surfaces.
          if (
            Array.isArray(value) &&
            column.isForeignKey &&
            column.isLoose &&
            column.sourceSchema &&
            column.sourceTable &&
            column.sourceColumn &&
            connectionId &&
            database &&
            !editModeRef.current?.isEditMode
          ) {
            const elements = value as unknown[];
            return (
              <ArrayFkCell
                elements={elements}
                visibleCount={MAX_ARRAY_FK_PILLS}
                connectionId={connectionId}
                database={database}
                sourceSchema={column.sourceSchema}
                sourceTable={column.sourceTable}
                sourceColumn={column.sourceColumn}
                surface={analyticsSurface ?? 'query_editor'}
                sourceRow={row.original}
                onOpenExpandedView={() =>
                  openExpandedTextDialog(
                    column.name,
                    JSON.stringify(elements, null, 2),
                    column.id,
                    row.original,
                    parseInt(row.id, 10),
                  )
                }
              />
            );
          }
          // Spatial cell: sniffer-derived summary + peek glyph. Placed
          // IMMEDIATELY BEFORE the JSON branch so spatial wins over JSON for the
          // same column. Returns null for non-spatial columns / unsniffable
          // values so we fall through to the JSON/default handling below.
          if (engine) {
            const rowIndex = parseInt(row.id, 10);
            // Windowed grids: only truncated STRING previews need the round-trip;
            // an already-complete marker object (Oracle/MSSQL) decodes directly.
            const fetchFullValue =
              getFullSpatialValue && typeof value === 'string'
                ? () => getFullSpatialValue(rowIndex, column.id)
                : undefined;
            const spatialCell = SpatialCellRenderer({
              value,
              columnId: column.id,
              columnName: column.name,
              rowData: row.original,
              engine,
              dataType: column.dataType,
              nativeSpatialColumnIds: nativeSpatialColumnIds ?? EMPTY_SET,
              declaredSpatialColumnIds: declaredSpatialColumnIds ?? EMPTY_SET,
              declaredSpatialColumns,
              fetchFullValue,
              connectionId,
              database,
              onOpenFull: (input) =>
                openExpandedTextDialog(
                  column.name,
                  String(value),
                  column.id,
                  row.original,
                  rowIndex,
                  { input, engine, dataType: column.dataType, fetchFullValue, connectionId, database },
                ),
            });
            if (spatialCell) return spatialCell;
          }
          // Shared JSON cell renderer: handles native parsed-object jsonb,
          // valid JSON strings, and the declared-on-text "invalid JSON" glyph.
          // Returns null for non-JSON columns so we fall through to the
          // generic object/string handling below.
          if (typeof value === 'object' || typeof value === 'string') {
            const jsonStr = typeof value === 'object' ? JSON.stringify(value) : String(value);
            const titleStr = truncateForTitle(jsonStr);
            const pathRels =
              connectionId &&
              database &&
              column.sourceSchema &&
              column.sourceTable
                ? findLooseRelationshipsForColumn(
                    metaInfo?.looseRelationships,
                    column.sourceSchema,
                    column.sourceTable,
                    column.sourceColumn ?? column.name,
                  ).filter((r) => r.sourcePath)
                : [];
            const baseContent = typeof value === 'object'
              ? <span title={titleStr} className="font-mono text-xs truncate min-w-0">{jsonStr}</span>
              : <span title={titleStr} className="truncate min-w-0">{jsonStr}</span>;
            const cellContent =
              pathRels.length > 0 && connectionId && database && column.sourceSchema && column.sourceTable ? (
                <span className="flex items-center gap-1 min-w-0">
                  {baseContent}
                  <JsonLooseFkBadges
                    connectionId={connectionId}
                    database={database}
                    value={value}
                    sourceSchema={column.sourceSchema}
                    sourceTable={column.sourceTable}
                    sourceColumn={column.sourceColumn ?? column.name}
                    rels={pathRels}
                    surface={analyticsSurface ?? 'query_editor'}
                  />
                </span>
              ) : (
                baseContent
              );
            const jsonCell = JsonCellRenderer({
              value,
              columnId: column.id,
              forcedJsonColumnIds: forcedJsonColumnIds ?? EMPTY_SET,
              declaredJsonColumnIds: declaredJsonColumnIds ?? EMPTY_SET,
              jsonColumnIdSet,
              onExpand: () => openExpandedTextDialog(
                column.name,
                jsonStr,
                column.id,
                row.original,
                parseInt(row.id, 10),
              ),
              cellContent,
            });
            if (jsonCell) return jsonCell;
          }

          if (typeof value === 'object') {
            const jsonStr = JSON.stringify(value);
            const titleStr = truncateForTitle(jsonStr);
            const isPlainObjOrArray = Array.isArray(value)
              || Object.getPrototypeOf(value) === Object.prototype
              || Object.getPrototypeOf(value) === null;
            if (isPlainObjOrArray || jsonStr.length >= 80) {
              return (
                <span className={CELL_EXPAND_WRAPPER_CLASS}>
                  <button
                    type="button"
                    className={CELL_EXPAND_BUTTON_CLASS}
                    title="View JSON"
                    aria-label="View JSON"
                    data-testid="qe-cell-expand-btn"
                    onClick={(event) => {
                      event.stopPropagation();
                      openExpandedTextDialog(column.name, jsonStr, column.id, row.original, parseInt(row.id, 10));
                    }}
                    onDoubleClick={(event) => { event.stopPropagation(); }}
                  >
                    <Code2 className="h-3 w-3" aria-hidden="true" />
                  </button>
                  <span title={titleStr} className="font-mono text-xs truncate min-w-0">{jsonStr}</span>
                </span>
              );
            }
            return <span title={titleStr} className="font-mono text-xs truncate min-w-0">{jsonStr}</span>;
          }
          // PP-01: display grouping is presentation-only. `rawDisplay` is what
          // the title attribute (and every copy/export path) keeps showing.
          const rawDisplay = String(value);
          const displayValue = isGroupedNumeric
            ? formatNumericDisplay(value, numericGrouping) ?? rawDisplay
            : rawDisplay;
          const displayTitle = truncateForTitle(rawDisplay);

          // Large text / multiline content
          if (typeof value === 'string' && shouldShowExpandIconForQueryResults(value, {
            isEditMode: editModeRef.current?.isEditMode && editModeRef.current?.isColumnEditable(column.id),
            dataType: column.dataType,
          })) {
            const looksJson = looksLikeJsonString(value);
            // E1 value-shape fallback: explicit `not-json` metadata suppresses
            // the JSON auto-format icon — fall back to the plain expand icon
            // and surface the "Looks like JSON" hint via the title tooltip.
            const notJsonColumn = isExplicitlyNotJsonColumn(column.jsonClassification);
            const isJson = looksJson && !notJsonColumn;
            const IconComponent = isJson ? Code2 : Maximize2;
            const valueTitle = !isJson && looksJson ? 'Looks like JSON' : undefined;
            return (
              <span className={CELL_EXPAND_WRAPPER_CLASS}>
                <button
                  type="button"
                  className={CELL_EXPAND_BUTTON_CLASS}
                  title={isJson ? 'View JSON' : 'View expanded'}
                  aria-label={isJson ? 'View JSON' : 'View expanded'}
                  data-testid="qe-cell-expand-btn"
                  onClick={(event) => {
                    event.stopPropagation();
                    openExpandedTextDialog(column.name, value, column.id, row.original, parseInt(row.id, 10));
                  }}
                  onDoubleClick={(event) => { event.stopPropagation(); }}
                >
                  <IconComponent className="h-3 w-3" aria-hidden="true" />
                </button>
                <span className="truncate min-w-0" title={valueTitle ?? displayTitle}>{displayValue}</span>
              </span>
            );
          }

          // E1 value-shape fallback: short JSON-shaped string in a column with
          // explicit `not-json` metadata — surface a tooltip but no formatting.
          if (
            typeof value === 'string' &&
            isExplicitlyNotJsonColumn(column.jsonClassification) &&
            looksLikeJsonString(value)
          ) {
            return <span title="Looks like JSON">{displayValue}</span>;
          }

          // FK cell with lazy popover
          if (
            column.isForeignKey &&
            column.sourceSchema &&
            column.sourceTable &&
            column.sourceColumn &&
            connectionId &&
            database &&
            !editModeRef.current?.isEditMode
          ) {
            // The lookup label rides AFTER the link, never inside it — the FK
            // cell's value, navigation and copy paths are untouched.
            return withLookupLabel(
              <LazyForeignKeyCell
                // Identity key. `LazyForeignKeyCell` caches the resolved FK
                // target in state and short-circuits on it ("if already
                // resolved, just open the popover"), and nothing resets that
                // on a prop change. Cell state now SURVIVES a columnDefs
                // rebuild (see `renderCellContent`), so without this key a new
                // result set landing at the same row+column but a different
                // source table would reuse the previous table's FK target and
                // navigate to the wrong table. Keyed on the identity the
                // target is derived from, so ordinary rebuilds still preserve
                // an open popover.
                key={`${connectionId}|${database}|${column.sourceSchema}|${column.sourceTable}|${column.sourceColumn}`}
                connectionId={connectionId}
                database={database}
                sourceSchema={column.sourceSchema}
                sourceTable={column.sourceTable}
                sourceColumn={column.sourceColumn}
                value={value}
                displayValue={displayValue}
                isLoose={column.isLoose}
                surface={analyticsSurface ?? 'query_editor'}
                sourceRow={row.original}
              />,
              lookupLabelInfos?.get(column.id),
              value,
              // The FK cell owns popover state — keep its wrapper present so a
              // label arriving later cannot unmount it mid-read.
              { stableWrapper: true },
            );
          }

          // Everything that isn't an interactive FK cell — including EVERY
          // cell once grid edit mode is on, which strips the FK link above.
          // The label has to survive that: the Table Explorer keeps rendering
          // it in edit mode (it gates per-cell on a pending edit, not on the
          // mode — `TableExplorerGridRow.tsx`), and a grid you are editing is
          // exactly where reading `3` as `Pending` matters most.
          // `withLookupLabel` returns `displayValue` untouched for every
          // non-lookup column, so no other cell changes shape.
          return withLookupLabel(displayValue, lookupLabelInfos?.get(column.id), value);
        },
      });
    }),
    [jsonColumnIdSet, forcedJsonColumnIds, declaredJsonColumnIds, nativeJsonColumnIds, declarableJsonColumnIds, onDeclaredGlyphClick, onNativeGlyphClick, onDeclarableGlyphClick, onLooseFkGlyphClick, declaredEnumColumnIds, nativeEnumColumnIds, hintedEnumColumnIds, onEnumGlyphClick, engine, nativeSpatialColumnIds, declaredSpatialColumnIds, declaredSpatialColumns, spatialDeclarableColumnIds, onSpatialGlyphClick, getFullSpatialValue, normalizedColumns, openExpandedTextDialog, sortState, isColumnPinned, unpinColumn, showColumnProfiling, columnProfiles, rows, connectionId, database, minColumnWidth, analyticsSurface, numericColumnIds, groupedNumericColumnIds, numericGrouping, metaInfo, lookupLabelInfos]
  );
}
